// TeamProductPickerField — shared product picker for the manual team
// create/edit dialogs (PRODOPSAI-542).
//
// Extracted to a single module so the picker block is not duplicated
// between CreateTeamDialog and EditTeamDialog (SonarCloud
// new_duplicated_lines_density gate).
//
// Behaviour mirrors AddMappingDialog: one productApi.getAll() fetch on
// mount, "(no product)" sentinel pinned to the top, real products
// sorted by name. The parent dialog stays usable even when the fetch
// fails — the picker shows an inline ErrorBanner but does not throw or
// block the surrounding form.

import { useEffect, useState } from 'react';
import type { IProduct } from '@skystone/shared';
import {
  ErrorBanner,
  SearchableSelect,
  Spinner,
  type SearchableOption,
} from '@/components/ui';
import { getErrorMessage } from '@/lib/utils';
import { productApi } from '@/services/product-api';

interface TeamProductPickerFieldProps {
  /** Controlled productId. Empty string = "(no product)" sentinel. */
  value: string;
  /** Fired with the picked productId (or empty string for sentinel). */
  onChange: (productId: string) => void;
  /** Stable id prefix for label + SearchableSelect + test-ids. */
  idPrefix: string;
}

function toProductSearchOptions(products: readonly IProduct[]): SearchableOption[] {
  // PRODOPSAI-542: a single explicit "(no product)" entry pinned to the top so
  // the admin can clear the field deliberately. Real products follow, sorted
  // by name (the backend already returns archived=false when includeArchived
  // isn't set).
  const realOptions: SearchableOption[] = products.map((p) => ({
    value: p.id,
    label: p.name,
  }));
  realOptions.sort((a, b) => a.label.localeCompare(b.label));
  return [{ value: '', label: '(no product)' }, ...realOptions];
}

export function TeamProductPickerField({
  value,
  onChange,
  idPrefix,
}: Readonly<TeamProductPickerFieldProps>) {
  const [products, setProducts] = useState<IProduct[]>([]);
  const [productsLoading, setProductsLoading] = useState<boolean>(false);
  const [productsError, setProductsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setProductsLoading(true);
    setProductsError(null);
    productApi
      .getAll({ includeArchived: false })
      .then((rows) => {
        if (cancelled === true) return;
        setProducts(rows);
      })
      .catch((err: unknown) => {
        if (cancelled === true) return;
        setProductsError(getErrorMessage(err, 'Failed to load products'));
      })
      .finally(() => {
        if (cancelled === false) setProductsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const productOptions = toProductSearchOptions(products);
  const triggerId = `${idPrefix}-product`;
  const loadingTestId = `${idPrefix}-product-loading`;
  const errorTestId = `${idPrefix}-product-error`;

  return (
    <div>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-sm font-medium">Product</span>
        {productsLoading === true && (
          <span data-testid={loadingTestId} className="inline-flex">
            <Spinner size="sm" />
          </span>
        )}
      </div>
      <SearchableSelect
        id={triggerId}
        testId={triggerId}
        placeholder="(no product)"
        options={productOptions}
        value={value}
        onChange={onChange}
      />
      {productsError !== null && (
        <div className="mt-2" data-testid={errorTestId}>
          <ErrorBanner message={productsError} />
        </div>
      )}
    </div>
  );
}
